import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { BENCHMARKS } from '../constants/benchmarks';
import { BUSINESS_METRICS } from '../constants/businessMetrics';

export function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
}

export function calculateROI(monthlyRevenue: number, marketingInvestment: number): number {
  const operationalCosts = monthlyRevenue * BUSINESS_METRICS.operationalCosts.percentage;
  const revenueAfterCosts = monthlyRevenue - operationalCosts;
  const netProfit = revenueAfterCosts * BUSINESS_METRICS.profitMargin.percentage;
  return marketingInvestment > 0 ? ((netProfit - marketingInvestment) / marketingInvestment) * 100 : 0;
}

export function getCACTicketRatioPerformanceLevel(ratio: number): {
  level: string;
  color: string;
  icon: React.ReactNode;
} {
  if (ratio >= BENCHMARKS.cacTicketRatio.ultraPerformance) {
    return {
      level: "🔥 Ultra Performance",
      color: "text-blue-500",
      icon: React.createElement(TrendingUp, { className: "h-4 w-4 text-blue-500" })
    };
  } else if (ratio >= BENCHMARKS.cacTicketRatio.excellence) {
    return {
      level: "🏆 Excelência",
      color: "text-green-500",
      icon: React.createElement(TrendingUp, { className: "h-4 w-4 text-green-500" })
    };
  } else if (ratio >= BENCHMARKS.cacTicketRatio.target) {
    return {
      level: "🎯 Meta Atingida",
      color: "text-green-400",
      icon: React.createElement(TrendingUp, { className: "h-4 w-4 text-green-400" })
    };
  } else if (ratio >= BENCHMARKS.cacTicketRatio.min) {
    return {
      level: "✅ Mínimo Aceitável",
      color: "text-yellow-500",
      icon: React.createElement(TrendingDown, { className: "h-4 w-4 text-yellow-500" })
    };
  }
  return {
    level: "💀 Abaixo do Mínimo",
    color: "text-red-500",
    icon: React.createElement(TrendingDown, { className: "h-4 w-4 text-red-500" })
  };
}

export function getConversionPerformanceLevel(conversionRate: number): {
  level: string;
  color: string;
  icon: React.ReactNode;
} {
  if (conversionRate >= BENCHMARKS.conversion.ultraPerformance) {
    return {
      level: "🔥 Ultra Performance",
      color: "text-blue-500",
      icon: React.createElement(TrendingUp, { className: "h-4 w-4 text-blue-500" })
    };
  } else if (conversionRate >= BENCHMARKS.conversion.excellence) {
    return {
      level: "🏆 Excelência",
      color: "text-green-500",
      icon: React.createElement(TrendingUp, { className: "h-4 w-4 text-green-500" })
    };
  } else if (conversionRate >= BENCHMARKS.conversion.target) {
    return {
      level: "🎯 Meta Atingida",
      color: "text-green-400",
      icon: React.createElement(TrendingUp, { className: "h-4 w-4 text-green-400" })
    };
  } else if (conversionRate >= BENCHMARKS.conversion.min) {
    return {
      level: "✅ Mínimo Aceitável",
      color: "text-yellow-500",
      icon: React.createElement(TrendingDown, { className: "h-4 w-4 text-yellow-500" })
    };
  }
  return {
    level: "💀 Baixa Conversão",
    color: "text-red-500",
    icon: React.createElement(TrendingDown, { className: "h-4 w-4 text-red-500" })
  };
}

export function getROIPerformanceLevel(roi: number): {
  level: string;
  color: string;
  icon: React.ReactNode;
} {
  if (roi >= BENCHMARKS.roi.ultraPerformance) {
    return {
      level: "🔥 Ultra Performance",
      color: "text-blue-500",
      icon: React.createElement(TrendingUp, { className: "h-4 w-4 text-blue-500" })
    };
  } else if (roi >= BENCHMARKS.roi.excellence) {
    return {
      level: "🏆 Excelência",
      color: "text-green-500",
      icon: React.createElement(TrendingUp, { className: "h-4 w-4 text-green-500" })
    };
  } else if (roi >= BENCHMARKS.roi.target) {
    return {
      level: "🎯 Meta Atingida",
      color: "text-green-400",
      icon: React.createElement(TrendingUp, { className: "h-4 w-4 text-green-400" })
    };
  } else if (roi >= BENCHMARKS.roi.min) {
    return {
      level: "✅ Mínimo Aceitável",
      color: "text-yellow-500",
      icon: React.createElement(TrendingDown, { className: "h-4 w-4 text-yellow-500" })
    };
  }
  return {
    level: "💀 ROI Negativo",
    color: "text-red-500",
    icon: React.createElement(TrendingDown, { className: "h-4 w-4 text-red-500" })
  };
}

export function getCPLPerformanceLevel(cpl: number): {
  level: string;
  color: string;
  icon: React.ReactNode;
} {
  if (cpl < BENCHMARKS.costPerLead.ultraPerformance) {
    return {
      level: "🔥 Ultra Performance",
      color: "text-blue-500",
      icon: React.createElement(TrendingUp, { className: "h-4 w-4 text-blue-500" })
    };
  } else if (cpl === BENCHMARKS.costPerLead.excellence) {
    return {
      level: "🏆 Excelência",
      color: "text-green-500",
      icon: React.createElement(TrendingUp, { className: "h-4 w-4 text-green-500" })
    };
  } else if (cpl <= BENCHMARKS.costPerLead.target) {
    return {
      level: "🎯 Meta Atingida",
      color: "text-green-400",
      icon: React.createElement(TrendingUp, { className: "h-4 w-4 text-green-400" })
    };
  } else if (cpl <= BENCHMARKS.costPerLead.min) {
    return {
      level: "✅ Mínimo Aceitável",
      color: "text-yellow-500",
      icon: React.createElement(TrendingDown, { className: "h-4 w-4 text-yellow-500" })
    };
  }
  return {
    level: "💀 Alto Custo",
    color: "text-red-500",
    icon: React.createElement(TrendingDown, { className: "h-4 w-4 text-red-500" })
  };
}

export function getPerformanceIndicator(value: number, benchmark: { min: number; target: number; max: number }): React.ReactNode {
  if (value >= benchmark.target) {
    return React.createElement(TrendingUp, { className: "h-4 w-4 text-green-500" });
  } else if (value >= benchmark.min) {
    return React.createElement(TrendingUp, { className: "h-4 w-4 text-yellow-500" });
  }
  return React.createElement(TrendingDown, { className: "h-4 w-4 text-red-500" });
}

export function getPerformanceClass(value: number, benchmark: { min: number; target: number; max: number }): string {
  if (value >= benchmark.target) {
    return "text-green-500";
  } else if (value >= benchmark.min) {
    return "text-yellow-500";
  }
  return "text-red-500";
}